/** Progress figures for a RUNNING validation: how far it has got and roughly how long is left.
 *  Pure functions over the summary's `progress` and the run's `startedAt`. */

import type { EnrichmentValidationProgress } from '../types/enrichment-validation.api.types';

/** Below this much run time the rate is mostly start-up (resolving the run, counting it), so no
 *  time-left estimate is shown yet. */
const MIN_ELAPSED_FOR_ESTIMATE_MS = 20_000;

export interface ProgressFigures {
  /** Whole percent checked, 0–100; `null` without an expected total. Floored, so 100 only once
   *  every expected product is checked. */
  percent: number | null;
  /** Products checked, never above the expected total (products added mid-run would pass it). */
  checked: number;
  /** Estimated milliseconds left at the run's average rate so far; `null` when there is no
   *  total, nothing checked yet, too little run time to tell, or nothing left. */
  remainingMs: number | null;
}

export function progressFigures(
  progress: EnrichmentValidationProgress,
  startedAt: string | null,
): ProgressFigures {
  const expected = progress.expectedProducts;
  if (expected === null || expected <= 0) {
    return { percent: null, checked: progress.checkedProducts, remainingMs: null };
  }
  const checked = Math.min(progress.checkedProducts, expected);
  const percent = Math.floor((checked * 100) / expected);
  return { percent, checked, remainingMs: estimateRemainingMs(progress, startedAt, checked) };
}

/** The run's average rate so far (checked / (last update − start)) applied to what is left. The
 *  rate comes from two recorded instants, not the clock, so it holds still between polls. */
function estimateRemainingMs(
  progress: EnrichmentValidationProgress,
  startedAt: string | null,
  checked: number,
): number | null {
  const expected = progress.expectedProducts ?? 0;
  if (!startedAt || checked <= 0 || checked >= expected) return null;
  const elapsed = Date.parse(progress.updatedAt) - Date.parse(startedAt);
  if (!Number.isFinite(elapsed) || elapsed < MIN_ELAPSED_FOR_ESTIMATE_MS) return null;
  return ((expected - checked) * elapsed) / checked;
}

/** A time-left estimate rounded for display: whole minutes, or hours and minutes. */
export type RemainingTime =
  | { unit: 'underMinute' }
  | { unit: 'minutes'; minutes: number }
  | { unit: 'hours'; hours: number; minutes: number };

export function roundRemaining(ms: number): RemainingTime {
  const minutes = Math.ceil(ms / 60_000);
  if (minutes <= 1) return { unit: 'underMinute' };
  if (minutes < 60) return { unit: 'minutes', minutes };
  return { unit: 'hours', hours: Math.floor(minutes / 60), minutes: minutes % 60 };
}
